<template>
  <ul>
    <li class="py-1 px-4 bg-grey-100 text-grey-500 uppercase tracking-wider text-sm font-medium border-b border-grey-200">Cities</li>
    <li v-for="place in places" :key="place.id" class="border-b border-grey-200">
      <div class="flex items-center px-4 py-3 cursor-pointer" @click="handleSelectPlace(place.id)">
        <p>
          <span class="font-medium">{{ place.mainText }}</span><span v-if="place.secondaryText">, {{ place.secondaryText }}</span>
        </p>
        <SvgAngleDown class="h-4 w-4 ml-auto transition-300" :class="{ 'rotate-180': selectedPlace === place.id }" />
      </div>

      <TransitionHeight :transition="transition">
        <div v-if="selectedPlace === place.id" :key="place.id + shownAirportCount" class="overflow-hidden">
          <ul>
            <AirportItem v-for="airport in displayedAirports" :key="airport.id" :airport="airport" @select="handleSelectAirport($event)" />
          </ul>
          <button
            v-if="selectedPlace === place.id && shownAirportCount < 10"
            @click="fetchMoreAirports(place.id)" class="pl-10 sm:px-16 pt-4 lg:pt-2 pb-5 text-xs font-medium underline text-grey-400 border-t border-grey-200 lg:border-t-0 w-full text-left"
          >
            Load more airports
          </button>
        </div>
      </TransitionHeight>
    </li>
  </ul>
</template>

<script>
import Vue from 'vue'
import { get, sync } from 'vuex-pathify'
import { getAirports } from '@/services/api'
import timeout from '@/utils/timeout'
import TransitionHeight from '@/components/transitions/Height'
import AirportItem from '@/components/AirportItem'
import SvgLocationPin from '@/components/svg/LocationPin'
import SvgAngleDown from '@/components/svg/AngleDown'

export default {
  components: { TransitionHeight, AirportItem, SvgLocationPin, SvgAngleDown },
  props: {
    otherAirport: { type: Object, required: false },
  },
  data () {
    return {
      shownAirportCount: 3,
      airportsPage: 1,
      transition: true,
    }
  },
  computed: {
    ...get('explorer/', ['places']),
    ...sync('explorer/', ['selectedPlace', 'placeAirports']),
    displayedAirports () {
      const placeAirports = this.placeAirports.find(place => place.id === this.selectedPlace)
      if (!placeAirports || !Array.isArray(placeAirports.airports)) return
      let filteredAirports = placeAirports.airports
      if (this.otherAirport) {
        filteredAirports = placeAirports.airports.filter(airport => airport.icao_code !== this.otherAirport.icao_code)
      }
      return filteredAirports.slice(0, this.shownAirportCount < 10 ? this.shownAirportCount : 10)
    }
  },
  watch: {
    selectedPlace: {
      immediate: true,
      handler () {
        this.airportsPage = 1
        this.shownAirportCount = 3
      },
    },
    places: {
      immediate: true,
      async handler (places) {
        if (!places.length) return

        // Handle first result
        const { id } = places[0]
        if (id === this.selectedPlace) return
        await this.fetchAirports(id)
        this.selectedPlace = id
      },
    },
  },
  methods: {
    async fetchAirports(placeId) {
      const { data } = await getAirports(placeId)
      this.placeAirports.push({ id: placeId, airports: data.data })
    },

    async fetchMoreAirports(placeId) {
      this.transition = false
      this.airportsPage++
      const { data } = await getAirports(placeId, this.airportsPage)
      const placeIndex = this.placeAirports.findIndex(place => place.id === placeId)
      const airports = this.placeAirports[placeIndex].airports.concat(data.data)
      const placeAirports = { id: this.placeAirports[placeIndex].id, airports }
      Vue.set(this.placeAirports, placeIndex, placeAirports)
      this.shownAirportCount += 3
    },

    async handleSelectPlace (placeId) {
      this.transition = true
      if (placeId === this.selectedPlace) {
        this.$nextTick(() => { this.selectedPlace = null })
      } else {
        await this.fetchAirports(placeId)
        this.selectedPlace = placeId
      }
    },

    async handleSelectAirport(airport) {
      if (! (airport.is_open && (airport.jetfly_pc12_operability !== 'unoperable' || airport.jetfly_pc24_operability !== 'unoperable' || airport.jetfly_sf50_operability !== 'unoperable'))) return
      this.$emit('select', airport)
    }
  }
}
</script>
